// /for-organisers: Photohawk Studio for race, event and competition organisers (English only, like /for-photographers). The page
// (src/pages/ForOrganisers.tsx) renders this copy, and its FAQPage structured data (src/lib/seo/structuredData.ts) is
// built from the same FAQ, so the two can't drift.
//
// Search intents it answers (our judgement; no volume data, docs/seo-content-plan.md): an event photography platform
// for organisers, race photos for participants, face recognition (selfie) photo search, bib number search, free race
// photos paid by a sponsor, and sharing photo revenue with photographers, clubs and charities.
//
// Copy rules (as for the site's other Studio pages): every claim is one the product makes today (checked against the
// code, 27 Oct 2026); only the site's approved figures; "originals", never full size; no competitor names; plan names
// only where a feature is plan-specific; British English.

import type { FaqItem } from "@/content/forParticipants/faq";

export const FOR_ORGANISERS_PATH = "/for-organisers";

export const ORGANISER_SEO = {
  title: "Event photography platform for organisers | Photohawk",
  description:
    "Give every participant their event photos, found by selfie, number or time: free or for sale, paid by a sponsor, with sales shared with your photographers.",
};

export const ORGANISER_FAQ: FaqItem[] = [
  {
    q: "Do we need our own photographers to use Photohawk?",
    a: "No. You can run your own workspace and invite the photographers you hire to upload into your galleries, or work with a photographer who already uses Photohawk Studio and share their gallery link. Either way, participants get one gallery to search.",
  },
  {
    q: "How do participants find their photos?",
    a: "By the number they wore, by a selfie, by the time they were there, or by browsing the collection for the part of the day they remember, such as the start, a match or the finish. Numbers are read and faces indexed automatically after upload, with no tagging by hand.",
  },
  {
    q: "What kinds of events is Photohawk used for?",
    a: "Running, cycling, triathlon, obstacle and trail events, football and other team sport, swimming, school sports days and graduations, dance and gymnastics, motorsport, equestrian, concerts, festivals and conferences: anywhere people want photos of themselves. For events with children, selfie search can be switched off for a gallery, and number search, time search and browsing keep working.",
  },
  {
    q: "Can participants download their photos for free?",
    a: "Yes. Each gallery can sell photos or give them away as free downloads, and some events mix the two. The organiser or a sponsor can pay for free downloads: they're charged per photo, and from 1 November 2026 every plan includes a monthly allowance (see pricing).",
  },
  {
    q: "How soon after the event are the photos online?",
    a: "With previews first, on every plan, light previews go up before the originals, so a gallery can be searchable within minutes of the photos coming off the course: a 10,000-photo gallery is live in about 8 minutes on a 20 Mbps upload. The originals follow in the background.",
  },
  {
    q: "Can we share photo sales with our photographers, club or charity?",
    a: "Yes, on the Max plan. Revenue splitting shares each gallery's sales with the people you choose: photographers for the photos they took, a team as a group, a club or charity for every sale, or a sponsor for one collection. Photohawk works out every share on every sale and pays people through its payout partner.",
  },
  {
    q: "Is face search allowed under GDPR?",
    a: "Selfie search uses biometric data, so it needs a lawful basis and clear information for participants. The selfie is used for that one search and then discarded. Our face data policy explains how Photohawk handles face data, and our GDPR checklist for organisers lists what to tell participants.",
  },
  {
    q: "Can the gallery carry our event's branding?",
    a: "Yes. Galleries carry the workspace's logo and colours on every plan, and Pro and Max can use your own domain. On Lite, a small “Powered by Photohawk” badge sits at the foot of the gallery.",
  },
];
